import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  TextField,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';

import { useNotifications } from '../../store/AppContext';
import {
  fetchPilotPreferences,
  createPilotPreference,
  deletePilotPreference
} from '../../api/services';
import { PilotPreference } from '../../types';
import { getPreferenceIcon, usePilotPreferenceIcons } from './StatusChips';
import { LocationPickerMap } from './LocationPickerMap';
import { reverseGeocode, geocode } from '../../utils/geocoding';
import { useI18n } from '../../i18n';

interface PilotPreferencesManagerProps {
  pilotId: string;
}

export function PilotPreferencesManager({ pilotId }: PilotPreferencesManagerProps) {
  const { t } = useI18n();
  const { showNotification } = useNotifications();
  const pilotPreferenceIcons = usePilotPreferenceIcons();
  const [preferences, setPreferences] = useState<PilotPreference[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    city: '',
    lat: '',
    lng: '',
    distance: 50, // default 50km
    icon: 'location',
  });

  const isInternalUpdate = useRef(false);

  // Debounced geocoding for city
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    const timer = setTimeout(async () => {
      if (formData.city) {
        const result = await geocode(formData.city);
        if (result) {
          isInternalUpdate.current = true;
          setFormData(prev => ({
            ...prev,
            lat: result.lat.toString(),
            lng: result.lng.toString()
          }));
        }
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [formData.city]);

  // Debounced reverse geocoding for lat/lng manual changes
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    const timer = setTimeout(async () => {
      const lat = parseFloat(formData.lat);
      const lng = parseFloat(formData.lng);

      if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
        const result = await reverseGeocode(lat, lng);
        if (result && result.city) {
          isInternalUpdate.current = true;
          setFormData(prev => ({
            ...prev,
            city: result.city || prev.city
          }));
        }
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [formData.lat, formData.lng]);

  const handleLocationSelect = async (lat: number, lng: number) => {
    isInternalUpdate.current = true;
    setFormData(prev => ({
      ...prev,
      lat: lat.toString(),
      lng: lng.toString()
    }));

    const result = await reverseGeocode(lat, lng);
    if (result && result.city) {
      isInternalUpdate.current = true;
      setFormData(prev => ({
        ...prev,
        city: result.city || ''
      }));
    }
  };

  const loadPreferences = async () => {
    setIsLoading(true);
    try {
      const response = await fetchPilotPreferences(pilotId);
      setPreferences(response.data || []);
    } catch (error: any) {
      showNotification(t('preferences.loadError'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (pilotId) {
      loadPreferences();
    }
  }, [pilotId]);

  const handleDelete = async (documentId: string) => {
    if (!window.confirm(t('preferences.deleteConfirm'))) return;

    try {
      await deletePilotPreference(documentId);
      showNotification(t('preferences.deleteSuccess'), 'success');
      setPreferences((prev) => prev.filter((p) => p.documentId !== documentId));
    } catch (error: any) {
      showNotification(t('preferences.deleteError'), 'error');
    }
  };

  const handleAdd = async () => {
    if (!formData.title.trim() || !formData.city.trim()) {
      showNotification(t('preferences.required'), 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: Partial<PilotPreference> = {
        title: formData.title,
        location: {
          city: formData.city,
          lat: formData.lat ? parseFloat(formData.lat) : undefined,
          lng: formData.lng ? parseFloat(formData.lng) : undefined,
        },
        distance: formData.distance * 1000, // convert km to meters
        icon: formData.icon,
      };

      await createPilotPreference(payload);
      showNotification(t('preferences.addSuccess'), 'success');
      setIsDialogOpen(false);
      setFormData({ title: '', city: '', lat: '', lng: '', distance: 50, icon: 'location' });
      loadPreferences();
    } catch (error: any) {
      showNotification(t('preferences.addError'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" fontWeight={600}>{t('preferences.title')}</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsDialogOpen(true)}
            size="small"
          >{t('preferences.add')}</Button>
        </Box>

        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>{t('preferences.info')}</Alert>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : preferences.length > 0 ? (
          <List disablePadding>
            {preferences.map((pref, index) => (
              <React.Fragment key={pref.id}>
                {index > 0 && <Divider component="li" />}
                <ListItem sx={{ py: 2 }}>
                  <Box sx={{ mr: 2, display: 'flex' }}>
                    {getPreferenceIcon(pref.icon)}
                  </Box>
                  <ListItemText
                    primary={pref.title}
                    secondary={`${pref.location?.city || t('preferences.noLocation')} (${t('preferences.radius')}: ${pref.distance / 1000} km)${pref.location?.lat ? ` [${pref.location.lat}, ${pref.location.lng}]` : ''}`}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => handleDelete(pref.documentId)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>{t('preferences.empty')}</Typography>
        )}
      </CardContent>

      <Dialog open={isDialogOpen} onClose={() => !isSubmitting && setIsDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('preferences.addDialogTitle')}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('preferences.titleLabel')}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                margin="dense"
              />
            </Grid>
            <Grid item xs={12}>
              <LocationPickerMap
                lat={parseFloat(formData.lat) || 0}
                lng={parseFloat(formData.lng) || 0}
                onLocationSelect={handleLocationSelect}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('preferences.cityLabel')}
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('preferences.latitudeLabel')}
                value={formData.lat}
                onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                margin="dense"
                placeholder={t('preferences.latitudePlaceholder')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('preferences.longitudeLabel')}
                value={formData.lng}
                onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                margin="dense"
                placeholder={t('preferences.longitudePlaceholder')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('preferences.distanceLabel')}
                type="number"
                value={formData.distance}
                onChange={(e) => setFormData({ ...formData, distance: parseInt(e.target.value) || 0 })}
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel>{t('preferences.iconLabel')}</InputLabel>
                <Select
                  value={formData.icon}
                  label={t('preferences.iconLabel')}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                >
                  {pilotPreferenceIcons.map((icon) => (
                    <MenuItem key={icon.id} value={icon.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {icon.component} {icon.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setIsDialogOpen(false)} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleAdd}
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? t('common.adding') : t('preferences.add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

